import React from 'react';
import { Card, CardContent } from '../../../components/ui/card';

const ApiReferenceSection = () => {
  return (
    <div className="mt-12" data-section="api-reference">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">API Reference</h2>
      <Card>
        <CardContent className="p-6">
          <div className="space-y-8">
            <CreateChangelogSection />
            <SearchChangelogSection />
            <ListChangelogSection />
            <ResponseStructureSection />
            <RateLimitsSection />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const CreateChangelogSection = () => (
  <div data-section="create-changelog">
    <h3 className="text-lg font-semibold text-gray-900 mb-4">Create Changelog Entry</h3>
    <div className="bg-gray-50 p-4 rounded-lg">
      <code className="text-sm text-gray-800">
        POST https://api.deltaops.app/api/deltaops/changelog/
      </code>
    </div>
    <div className="mt-4">
      <h4 className="font-medium text-gray-900 mb-2">Request Body</h4>
      <div className="bg-gray-50 p-4 rounded-lg overflow-x-auto">
        <pre className="text-sm text-gray-800">
{`{
  "title": string,        // Required: Brief description of the change
  "description": string,  // Required: Detailed explanation of what changed
  "tags": string[],      // Optional: Array of tags to categorize the change
  "source_type": string, // Optional: Type of change (e.g., "deployment", "config")
  "source_link": string, // Optional: URL to the source (e.g., Git commit, Jira ticket)
  "environment": string, // Optional: Environment where change occurred
  "severity": string,    // Optional: "low", "medium", "high", or "critical"
  "is_critical": bool,   // Optional: Whether this is a critical change
  "event_date": string,  // Optional: ISO 8601 timestamp when the change occurred
  "metadata": object     // Optional: Any additional structured data
}`}
        </pre>
      </div>
    </div>
  </div>
);

const SearchChangelogSection = () => (
  <div data-section="search-changelog">
    <h3 className="text-lg font-semibold text-gray-900 mb-4">Search Changelog Entries</h3>
    <div className="bg-gray-50 p-4 rounded-lg">
      <code className="text-sm text-gray-800">
        POST https://api.deltaops.app/api/deltaops/changelog/search/
      </code>
    </div>
    <div className="mt-4">
      <h4 className="font-medium text-gray-900 mb-2">Request Body</h4>
      <div className="bg-gray-50 p-4 rounded-lg overflow-x-auto">
        <pre className="text-sm text-gray-800">
{`{
  "query": string,       // Required: Search query text
  "top_k": number,      // Optional: Number of results to return (default: 5)
  "weights": {          // Optional: Custom field weights for search
    "title": number,    // Weight for title field (0-1)
    "description": number,
    "tags": number
  },
  "decay_factor": number, // Optional: Temporal decay factor (default: 0.1)
  "filters": {           // Optional: Filter conditions
    "severity": string,
    "source_type": string,
    "is_critical": boolean,
    "tags": string[],
    "date_range": {
      "start": string,  // ISO 8601 date
      "end": string     // ISO 8601 date
    }
  }
}`}
        </pre>
      </div>
    </div>
  </div>
);

const ListChangelogSection = () => (
  <div data-section="list-changelog">
    <h3 className="text-lg font-semibold text-gray-900 mb-4">List Changelog Entries</h3>
    <div className="bg-gray-50 p-4 rounded-lg">
      <code className="text-sm text-gray-800">
        GET https://api.deltaops.app/api/deltaops/changelog/
      </code>
    </div>
    <div className="mt-4">
      <h4 className="font-medium text-gray-900 mb-2">Query Parameters</h4>
      <div className="bg-gray-50 p-4 rounded-lg overflow-x-auto">
        <pre className="text-sm text-gray-800">
{`{
  "page": number,     // Optional: Page number (default: 1)
  "limit": number     // Optional: Items per page (default: 10)
}`}
        </pre>
      </div>
    </div>
  </div>
);

const ResponseStructureSection = () => (
  <div data-section="response-structure">
    <h3 className="text-lg font-semibold text-gray-900 mb-2">Response Structure</h3>
    <div className="bg-gray-50 p-4 rounded-lg overflow-x-auto">
      <pre className="text-sm text-gray-800">
{`{
  "id": string,           // Unique identifier for the change event
  "title": string,        // Brief description of the change
  "description": string,  // Detailed explanation
  "tags": string[],      // Array of tags
  "created_at": string,  // ISO 8601 timestamp of creation
  "updated_at": string,  // ISO 8601 timestamp of last update
  "event_date": string,  // ISO 8601 timestamp of when change occurred
  "source_type": string,
  "source_link": string,
  "environment": string,
  "severity": string,
  "is_critical": boolean,
  "metadata": object
}`}
      </pre>
    </div>
  </div>
);

const RateLimitsSection = () => (
  <div data-section="rate-limits">
    <h3 className="text-lg font-semibold text-gray-900 mb-2">Rate Limits</h3>
    <div className="space-y-2 text-gray-600">
      <p>Rate limits vary by plan:</p>
      <ul className="list-disc list-inside space-y-1">
        <li>Free: 100 requests per month</li>
        <li>Pro: Unlimited requests, max 10 requests per second</li>
        <li>Enterprise: Custom limits available</li>
      </ul>
    </div>
  </div>
);

export default ApiReferenceSection;